import { useTranslation } from 'react-i18next';
import PackageStatusBadge from './PackageStatusBadge';
import dateUtils from '../../../Utils/dateUtils';
import { VossPackageTypeText } from '../../../Constants/VOSS/vossPackageType';
import type { PackageSearchHit } from './Packages.types';

interface PackagesTableProps {
    packages: PackageSearchHit[];
    onSelect: (packageId: string) => void;
}

const PackagesTable = ({ packages, onSelect }: PackagesTableProps) => {
    const { t } = useTranslation();

    if (packages.length === 0)
        return (
            <p className="my-16">
                <i>{t('packages.noPackagesFound')}</i>
            </p>
        );

    return (
        <table className="table table-stripe skynet-table-no-divider table-active">
            <thead>
                {/* col-* sets max-width, which is what constrains column widths here — the design system has no table width helpers */}
                <tr>
                    <th className="col-4">{t('common.name')}</th>
                    <th className="col-1">{t('packages.type')}</th>
                    <th className="col-5">{t('packages.targetGroups')}</th>
                    <th className="col-1">{t('common.status')}</th>
                    <th className="col-1">{t('packages.created')}</th>
                </tr>
            </thead>
            <tbody>
                {packages.map((vossPackage) => {
                    const externalId = vossPackage.externalId ?? '';

                    return (
                        <tr
                            key={vossPackage.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelect(vossPackage.id ?? '')}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onSelect(vossPackage.id ?? '');
                                }
                            }}
                        >
                            <td className="py-12">
                                <div className="font-weight-600">{vossPackage.name}</div>
                                <div className={`text-secondary font-size-xs mt-4${externalId.length > 0 ? '' : ' font-style-italic'}`}>
                                    {externalId.length > 0 ? externalId : t('packages.noExternalId')}
                                </div>
                            </td>
                            <td className="py-12">{vossPackage.type === undefined ? '' : VossPackageTypeText[vossPackage.type]}</td>
                            <td className="py-12">
                                <span className="text-secondary font-13">{(vossPackage.targetGroups ?? []).map((group) => group.name).join(' · ')}</span>
                            </td>
                            <td className="py-12">
                                <PackageStatusBadge status={vossPackage.status} />
                            </td>
                            <td className="py-12">{dateUtils.formatDateOrPlaceholder(vossPackage.versionCreated)}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

export default PackagesTable;
